/**
 * 独立投诉举报表单页 - 通过分享链接/二维码打开，无需登录即可提交
 * 提交后写入服务端「监察审计组投诉举报信息」多维表
 */
import React, { useState } from 'react';

const CATEGORIES = [
  { value: '工作作风', label: '工作作风' },
  { value: '廉洁纪律', label: '廉洁纪律' },
  { value: '制度执行', label: '制度执行' },
  { value: '其他', label: '其他' },
];

export default function ComplaintFormStandalone() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('工作作风');
  const [anonymous, setAnonymous] = useState(false);
  const [reporterName, setReporterName] = useState('');
  const [contact, setContact] = useState('');
  const [sendCodeToEmail, setSendCodeToEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [queryCode, setQueryCode] = useState('');
  const [codeSentToEmail, setCodeSentToEmail] = useState(false);
  const [mailWarning, setMailWarning] = useState('');
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const isEmailContact = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.trim());

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError(`附件「${file.name}」超过 50MB，无法上传`);
      return;
    }
    const base = typeof window !== 'undefined' ? (window.__BASE_URL__ || '') : '';
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${base}/api/pxcharts/public/complaint/upload`, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || `附件上传失败 ${res.status}`);
    }
    return data.attachment;
  };

  const handlePickAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError('');
    if (attachments.length + files.length > 5) {
      setError('最多可上传 5 个附件');
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const item = await uploadAttachment(f);
        if (item) uploaded.push(item);
      }
      if (uploaded.length) {
        setAttachments((prev) => [...prev, ...uploaded]);
      }
    } catch (err) {
      setError(err.message || '附件上传失败');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((x) => x.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      setError('请填写投诉内容');
      return;
    }
    if (!anonymous && !contact.trim()) {
      setError('选择非匿名投诉时，请填写联系方式');
      return;
    }
    if (sendCodeToEmail && !isEmailContact) {
      setError('若需发送投诉码到邮箱，请先填写有效邮箱地址');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const base = typeof window !== 'undefined' ? (window.__BASE_URL__ || '') : '';
      const res = await fetch(`${base}/api/pxcharts/public/complaint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          category,
          anonymous,
          reporterName: reporterName.trim(),
          contact: contact.trim(),
          sendCodeToEmail,
          attachments,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `提交失败 ${res.status}`);
      setQueryCode(data.queryCode || '');
      setCodeSentToEmail(!!data.codeSentToEmail);
      setMailWarning(String(data.warning || '').trim());
      setDone(true);
    } catch (err) {
      setError(err.message || '网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const statusUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/complaint-status`
      : '';
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#fff7ec] via-[#fffaf3] to-white px-4 py-8">
        <div className="mx-auto max-w-xl rounded-2xl border border-[#f3dfbf] bg-white shadow-[0_10px_30px_rgba(141,88,19,0.08)]">
          <div className="rounded-t-2xl bg-gradient-to-r from-[#a5161b] via-[#c11f24] to-[#8f1217] px-6 py-5 text-white">
            <div className="text-xs tracking-[0.2em] text-[#ffdca8]">监察审计组</div>
            <h2 className="mt-2 text-xl font-semibold">投诉举报提交成功</h2>
            <p className="mt-1 text-sm text-[#ffe9ce]">感谢您的监督反馈，我们将按流程及时受理。</p>
          </div>
          <div className="space-y-4 px-6 py-6">
            <div className="rounded-xl border border-[#f2e4cc] bg-[#fff9ef] p-4">
              <p className="text-sm font-semibold text-[#8a4c08]">查询码（请妥善保管）</p>
              <p className="mt-2 rounded-lg border border-[#f0d8b0] bg-white px-3 py-2 font-mono text-lg font-bold tracking-wide text-[#8f1217]">
                {queryCode || '未生成'}
              </p>
              <p className="mt-2 text-xs leading-5 text-[#8a5a25]">
                {contact.trim()
                  ? '您可使用“联系方式 + 查询码”查询受理进度。'
                  : '您未留联系方式，可仅凭查询码查询受理进度。'}
              </p>
              {codeSentToEmail && (
                <p className="mt-1 text-xs text-emerald-700">投诉码已发送至邮箱，请注意查收。</p>
              )}
              {!codeSentToEmail && mailWarning && (
                <p className="mt-1 text-xs text-red-600">{mailWarning}</p>
              )}
            </div>
            <a
              href={statusUrl}
              className="inline-flex cursor-pointer items-center rounded-lg bg-[#b91c1c] px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#991b1b]"
            >
              前往查询受理状态
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fff7ec] via-[#fffaf3] to-white px-4 py-8">
      <div className="mx-auto max-w-3xl rounded-2xl border border-[#f3dfbf] bg-white shadow-[0_12px_36px_rgba(141,88,19,0.1)]">
        <div className="rounded-t-2xl bg-gradient-to-r from-[#a5161b] via-[#c11f24] to-[#8f1217] px-6 py-6 text-white">
          <p className="text-xs tracking-[0.2em] text-[#ffdca8]">SUNNER SUPERVISION</p>
          <h1 className="mt-2 text-2xl font-semibold">监察投诉与建议登记</h1>
          <p className="mt-2 text-sm leading-6 text-[#ffe9ce]">
            请如实填写投诉或建议内容。系统将按企业监察流程受理，并对举报信息严格保密。
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-[#4b1d10]">投诉主题（选填）</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="例如：关于某部门流程执行问题的反映"
                className="w-full rounded-lg border border-[#e8d8bd] bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-[#c11f24] focus:outline-none focus:ring-2 focus:ring-[#f7d7a6]"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-[#4b1d10]">投诉类别</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-[#e8d8bd] bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-[#c11f24] focus:outline-none focus:ring-2 focus:ring-[#f7d7a6]"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 rounded-lg border border-[#efe0c8] bg-[#fffaf2] px-3 py-2.5">
              <input
                type="checkbox"
                id="anonymous"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#b91c1c] focus:ring-[#b91c1c]"
              />
              <label htmlFor="anonymous" className="cursor-pointer text-sm font-medium text-[#5b2b16]">匿名提交</label>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-[#4b1d10]">
                举报人姓名 {anonymous ? '（匿名可不填）' : '（选填）'}
              </label>
              <input
                type="text"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
                placeholder={anonymous ? '匿名提交可不填姓名' : '可填写姓名便于后续核实'}
                className="w-full rounded-lg border border-[#e8d8bd] bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-[#c11f24] focus:outline-none focus:ring-2 focus:ring-[#f7d7a6]"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-[#4b1d10]">
                联系方式 {anonymous ? '（选填）' : '（非匿名必填）'}
              </label>
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={anonymous ? '可填邮箱或手机号（便于回访）' : '请填写邮箱或手机号'}
                className="w-full rounded-lg border border-[#e8d8bd] bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-[#c11f24] focus:outline-none focus:ring-2 focus:ring-[#f7d7a6]"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-[#4b1d10]">
                具体内容 <span className="text-red-600">*</span>
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请详细描述投诉事实、涉及时间地点、相关人员及证据线索。"
                rows={6}
                required
                className="w-full rounded-lg border border-[#e8d8bd] bg-white px-4 py-3 text-sm leading-6 text-gray-800 focus:border-[#c11f24] focus:outline-none focus:ring-2 focus:ring-[#f7d7a6]"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-[#4b1d10]">
                上传附件（单个不超过50MB，最多5个）
              </label>
              <input
                type="file"
                multiple
                onChange={handlePickAttachments}
                disabled={uploading}
                className="w-full rounded-lg border border-[#e8d8bd] bg-white px-3 py-2 text-sm text-gray-800 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-[#b91c1c] file:px-3 file:py-1.5 file:text-white"
              />
              {uploading && <p className="mt-1 text-xs text-[#8a5a25]">附件上传中，请稍候...</p>}
              {!!attachments.length && (
                <div className="mt-2 space-y-1 rounded-lg border border-[#efe0c8] bg-[#fffaf2] p-2">
                  {attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-[#5b2b16]">
                        {att.originalName}（{(Number(att.size || 0) / (1024 * 1024)).toFixed(2)}MB）
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(att.id)}
                        className="rounded border border-red-200 px-2 py-0.5 text-red-600 hover:bg-red-50"
                      >
                        移除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-[#efe0c8] bg-[#fffaf2] px-4 py-3">
            <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-[#5b2b16]">
              <input
                type="checkbox"
                id="sendCodeToEmail"
                checked={sendCodeToEmail}
                onChange={(e) => setSendCodeToEmail(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#b91c1c] focus:ring-[#b91c1c]"
              />
              将查询码发送到我的邮箱
            </label>
            <p className="mt-1 text-xs text-[#8a5a25]">勾选后，若邮箱格式正确，系统会在提交后同步发送查询码邮件。</p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs leading-5 text-[#8a5a25]">
              提交即表示您确认所填信息真实有效。恶意举报、诬告将按公司制度处理。
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="min-w-[148px] cursor-pointer rounded-lg bg-[#b91c1c] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#991b1b] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? '提交中...' : '提交投诉/建议'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
